import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TextureOutlined, ShoppingBag, SwapHoriz } from '@mui/icons-material';

import { Order } from '../../../models/Order';
import { CartProduct } from '../../cart/Cart';

type Props = {
  order?: Order;
  products?: CartProduct[];
  deliveryPrice?: number;
  showSummary?: boolean;
};

function PriceList({
  order,
  products,
  deliveryPrice,
  showSummary = true,
}: Props) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  if (order !== undefined) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
      >
        <div className="p-4 border-b border-gray-100 flex items-center">
          <ShoppingBag className="mr-3 text-gray-400" />
          <h2 className="font-medium text-gray-800">Podsumowanie zamówienia</h2>
        </div>

        <div className="divide-y divide-gray-100">
          {order.items.map((item, index) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className="relative p-3 flex gap-3"
            >
              {/* Niestandardowy produkt - badge */}
              {item.product.packaged === false && (
                <motion.div
                  className="absolute top-2 right-2 z-10 bg-amber-100 rounded-md px-2 py-1 flex items-center gap-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <TextureOutlined
                    fontSize="small"
                    className="text-amber-600"
                  />
                  <span className="text-xs font-medium text-amber-800">
                    Niestandardowy
                  </span>
                </motion.div>
              )}

              {/* Returned items badge */}
              {item.returnedQuantity > 0 && (
                <motion.div
                  className="absolute top-2 right-2 z-10 bg-blue-100 rounded-md px-2 py-1 flex items-center gap-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <SwapHoriz fontSize="small" className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-800">
                    Zwrócono: {item.returnedQuantity}
                  </span>
                </motion.div>
              )}

              <Link
                to={`/produkt/${item.product.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex-shrink-0 w-16 h-16 overflow-hidden rounded-md bg-gray-50 border border-gray-100"
              >
                <motion.img
                  src={item.product.mobilePrimaryImage}
                  alt={item.product.name}
                  className="w-full h-full object-contain p-1 mix-blend-multiply"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>

              <div className="flex-grow flex flex-col">
                <div className="mb-1">
                  <Link
                    className="group"
                    to={`/produkt/${item.product.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h3 className="text-sm font-medium text-gray-800 group-hover:text-primary transition-colors line-clamp-2">
                      {item.product.name}
                    </h3>
                    <div className="w-0 group-hover:w-full h-[1px] bg-primary transition-all duration-300"></div>
                  </Link>
                </div>

                <div className="flex items-center justify-between mt-auto">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 text-sm">
                      {parseFloat(item.productPrice as any).toFixed(2)} zł
                    </span>
                    <span className="text-xs text-gray-500">
                      Netto: {(item.productPrice * 0.77).toFixed(2)} zł
                    </span>
                  </div>

                  <div className="flex items-center">
                    <span className="text-xs text-gray-500 mr-2">Ilość:</span>
                    <span className="text-sm font-medium py-1 px-2 bg-gray-100 rounded-full flex items-center">
                      {item.quantity}
                      {item.returnedQuantity > 0 && (
                        <span className="text-xs text-blue-600 ml-1">
                          (-{item.returnedQuantity})
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {item.returnedQuantity > 0 && (
                  <div className="mt-1.5 text-xs text-blue-600">
                    Aktualna ilość: {item.quantity - item.returnedQuantity}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {showSummary && (
          <motion.div
            variants={itemVariants}
            className="bg-gray-50 p-4 border-t border-gray-100"
          >
            <div className="space-y-2 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Wartość produktów:</span>
                <span className="font-medium">{order.subTotalPrice} zł</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dostawa:</span>
                <span className="font-medium">{order.deliveryPrice} zł</span>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-800 font-medium">Do zapłaty:</span>
                <span className="text-lg font-bold text-primary">
                  {order.totalPrice} zł
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Netto:</span>
                <span>{(order.totalPrice * 0.77).toFixed(2)} zł</span>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  if (products !== undefined && deliveryPrice !== undefined) {
    const subTotal = products.reduce(
      (acc, product) => acc + product.price * product.quantity,
      0,
    );

    const totalPrice = subTotal + deliveryPrice;
    const netto = totalPrice * 0.77;

    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
      >
        <div className="p-4 border-b border-gray-100 flex items-center">
          <ShoppingBag className="mr-3 text-gray-400" />
          <h2 className="font-medium text-gray-800">Podsumowanie zamówienia</h2>
        </div>

        <div className="divide-y divide-gray-100">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              className="relative p-3 flex gap-3"
            >
              {/* Niestandardowy produkt - badge */}
              {!product.packaged && (
                <motion.div
                  className="absolute top-2 right-2 z-10 bg-amber-100 rounded-md px-2 py-1 flex items-center gap-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <TextureOutlined
                    fontSize="small"
                    className="text-amber-600"
                  />
                  <span className="text-xs font-medium text-amber-800">
                    Niestandardowy
                  </span>
                </motion.div>
              )}

              <Link
                to={`/produkt/${product.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex-shrink-0 w-16 h-16 overflow-hidden rounded-md bg-gray-50 border border-gray-100"
              >
                <motion.img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain p-1 mix-blend-multiply"
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                />
              </Link>

              <div className="flex-grow flex flex-col">
                <div className="mb-1">
                  <Link
                    className="group"
                    to={`/produkt/${product.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h3 className="text-sm font-medium text-gray-800 group-hover:text-primary transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <div className="w-0 group-hover:w-full h-[1px] bg-primary transition-all duration-300"></div>
                  </Link>
                </div>

                <div className="flex items-center justify-between mt-auto">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 text-sm">
                      {product.price.toFixed(2)} zł
                    </span>
                    <span className="text-xs text-gray-500">
                      Netto: {(product.price * 0.77).toFixed(2)} zł
                    </span>
                  </div>

                  <div className="flex items-center">
                    <span className="text-xs text-gray-500 mr-2">Ilość:</span>
                    <span className="text-sm font-medium py-1 px-2 bg-gray-100 rounded-full">
                      {product.quantity}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          variants={itemVariants}
          className="bg-gray-50 p-4 border-t border-gray-100"
        >
          <div className="space-y-2 mb-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Wartość produktów:</span>
              <span className="font-medium">{subTotal.toFixed(2)} zł</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Dostawa:</span>
              <span className="font-medium">{deliveryPrice.toFixed(2)} zł</span>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-medium">Do zapłaty:</span>
              <span className="text-lg font-bold text-primary">
                {totalPrice.toFixed(2)} zł
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Netto:</span>
              <span>{netto.toFixed(2)} zł</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return null;
}

export default PriceList;
